/**
 * @file draft-po-timeline.tsx
 * @module app/modules/uProcure/po-management/draft-po-timeline
 * @description Compact timeline for a draft PO: counts per action (click to filter), search by
 *   product / person / note, entries grouped by day, latest 10 first with "Show all". Nothing is
 *   dropped: every event stays reachable through the filters and "Show all".
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useMemo, useState } from "react";
import { Button, Card } from "react-bootstrap";
import Method from "../../../../utils/methods";
import { TimelineEvent } from "./draft-po.types";

export const TIMELINE_ACTIONS: Record<string, { label: string; color: string }> = {
  created: { label: "Created", color: "primary" },
  product_added: { label: "Product added", color: "success" },
  product_removed: { label: "Product removed", color: "warning" },
  quantity_changed: { label: "Quantity changed", color: "info" },
  supplier_changed: { label: "Supplier changed", color: "info" },
  submitted: { label: "Submitted", color: "primary" },
  deleted: { label: "Deleted", color: "danger" },
  quantity_type_changed: { label: "Qty type changed", color: "info" },
  status_changed: { label: "Status changed", color: "primary" },
  grn_cost_changed: { label: "GRN cost differs", color: "danger" },
};

const PAGE = 10;
/** Day / time of an entry; entries without a date are grouped under "No date". */
const dayOf = (d?: string) => (d ? Method.convertDateToFormat(d, "DD/MM/YYYY") : "No date");
const timeOf = (d?: string) => (d ? Method.convertDateToFormat(d, "hh:mm A") : "-");

const DraftPoTimeline = ({ events, title = "Timeline" }: { events: TimelineEvent[]; title?: string }) => {
  const [action, setAction] = useState<string>("");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  const counts = useMemo(
    () => events.reduce<Record<string, number>>((acc, e) => ((acc[e.action] = (acc[e.action] ?? 0) + 1), acc), {}),
    [events]
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...events]
      .reverse()
      .filter((e) => !action || e.action === action)
      .filter(
        (e) =>
          !q ||
          [e.title, e.by?.name, e.note].some((v) => String(v ?? "").toLowerCase().includes(q))
      );
  }, [events, action, search]);
  const visible = showAll ? filtered : filtered.slice(0, PAGE);
  const byDay = visible.reduce<{ day: string; items: TimelineEvent[] }[]>((acc, e) => {
    const day = dayOf(e.at);
    const last = acc[acc.length - 1];
    if (last && last.day === day) last.items.push(e);
    else acc.push({ day, items: [e] });
    return acc;
  }, []);

  return (
    <Card className="border">
      <Card.Header className="min-h-50px flex-wrap gap-3 align-items-center">
        <Card.Title className="fs-17 fw-bolder m-0">{title} ({events.length})</Card.Title>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <button
            type="button"
            className={`btn btn-sm ${!action ? "btn-dark" : "btn-light"}`}
            onClick={() => setAction("")}
          >
            All {events.length}
          </button>
          {Object.entries(counts).map(([a, n]) => (
            <button
              key={a}
              type="button"
              className={`btn btn-sm ${action === a ? `btn-${TIMELINE_ACTIONS[a]?.color ?? "secondary"}` : "btn-light"}`}
              onClick={() => setAction(action === a ? "" : a)}
            >
              {TIMELINE_ACTIONS[a]?.label ?? a} {n}
            </button>
          ))}
          <input
            className="form-control form-control-sm w-200px"
            placeholder="Search product, person, note"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </Card.Header>
      <Card.Body className="py-3" style={{ maxHeight: showAll ? 520 : undefined, overflowY: showAll ? "auto" : undefined }}>
        {!filtered.length && <div className="text-gray-600 fs-14 py-4">No timeline entries match</div>}
        {byDay.map((g) => (
          <div key={g.day} className="mb-3">
            <div className="fs-13 fw-bold text-gray-700 bg-light px-3 py-1 rounded mb-1">{g.day}</div>
            {g.items.map((e, i) => (
              <div
                key={i}
                className={`d-flex gap-3 py-2 px-3 border-bottom border-gray-200 fs-14 align-items-start ${
                  e.action === "grn_cost_changed" ? "bg-light-danger" : ""
                }`}
              >
                <span className="text-gray-600 fs-13 text-nowrap" style={{ minWidth: 70 }}>
                  {timeOf(e.at)}
                </span>
                <span
                  className={`badge badge-light-${TIMELINE_ACTIONS[e.action]?.color ?? "secondary"} text-nowrap`}
                  style={{ minWidth: 120 }}
                >
                  {TIMELINE_ACTIONS[e.action]?.label ?? e.action}
                </span>
                <div className="flex-grow-1">
                  {e.title && <span className="fw-600">{e.title}</span>}
                  {e.from !== undefined && e.from !== null && e.to !== undefined && e.to !== null ? (
                    <span> · {String(e.from)} → {String(e.to)}</span>
                  ) : e.to !== undefined && e.to !== null ? (
                    <span> · qty {String(e.to)}</span>
                  ) : e.from !== undefined && e.from !== null ? (
                    <span> · was {String(e.from)}</span>
                  ) : null}
                  {e.note && <div className="fs-13 text-gray-600 text-truncate" style={{ maxWidth: 760 }} title={e.note}>{e.note}</div>}
                </div>
                <span className="fs-13 text-gray-700 text-nowrap">{e.by?.name || "-"}</span>
              </div>
            ))}
          </div>
        ))}
        {filtered.length > PAGE && (
          <div className="text-center pt-2">
            <Button size="sm" variant="light" onClick={() => setShowAll(!showAll)}>
              {showAll ? "Show latest 10" : `Show all ${filtered.length}`}
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default DraftPoTimeline;
